import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  // VALUES
  static values = {
    espro: Boolean,
    invalidMessage: String,
  };

  // TARGETS
  static targets = [
    "nationality",
    "documentType",
    "documentNumber",
    "documentCountry",
    "migrationRegistrationNumberWrapper",
    "errorMessage"
  ];

  // CONFIG
  get config() {
    return {
      cpf: {
        mask: (v) =>
          v
            .replace(/^(\d{3})(\d)/, "$1.$2")
            .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4"),
        validate: this.isValidCPF.bind(this),
        length: 11,
      },
      nif: {
        mask: (v) => v.replace(/^(\d{3})(\d{3})(\d{3})/, "$1 $2 $3"),
        validate: this.isValidNIF.bind(this),
        length: 9,
      },
      cuit: {
        mask: (v) =>
          v.replace(/^(\d{2})(\d)/, "$1-$2").replace(/-(\d{8})(\d)/, "-$1-$2"),
        validate: this.isValidCUIT.bind(this),
        length: 11,
      },
      cuil: {
        mask: (v) =>
          v.replace(/^(\d{2})(\d)/, "$1-$2").replace(/-(\d{8})(\d)/, "-$1-$2"),
        validate: this.isValidCUIT.bind(this), // Mesma lógica do CUIT
        length: 11,
      },
    };
  }

  // CONNECT
  connect() {
    if (this.documentTypeTarget?.value && this.documentNumberTarget.value) {
      this.applyMaskAndValidate();
    }

    if (this.hasMigrationRegistrationNumberWrapperTarget) {
      this.migrationRegistrationHiddenField =
        this.migrationRegistrationNumberWrapperTarget.querySelector(
          ".migration-registration-field"
        );
      this.displayMigrationRegistrationInput();
    }

    this.setDocumentTypeByCountryTarget();
  }

  // HANDLES
  submit(event) {
    if (!this.hasDocumentTypeTarget || !this.hasDocumentNumberTarget) return;

    const type = this.documentTypeTarget.value;
    const value = this.documentNumberTarget.value.replace(/\D/g, "");

    if (this.config[type] && !this.config[type].validate(value)) {
      event.preventDefault();
      this.setInvalid();
      this.documentNumberTarget.focus();
      this.resetFormSubmitButton(event);
    }
  }

  resetFormSubmitButton(event) {
    const form = event?.target?.closest("form") || this.element.closest("form");
    if (!form) return;

    const submitBtn = form.querySelector('[data-registration-target="submitButton"]') ||
                      form.querySelector('input[type="submit"], button[type="submit"]');
    if (!submitBtn) return;

    submitBtn.disabled = false;
    submitBtn.removeAttribute("disabled");

    const originalText = submitBtn.dataset.originalText;
    if (!originalText) return;

    if (submitBtn.tagName === "INPUT") {
      submitBtn.value = originalText;
      return;
    }

    submitBtn.innerHTML = originalText;
  }

  nationalityChanged() {
    this.setDocumentTypeByCountryTarget();
    this.displayMigrationRegistrationInput();
    this.clearValidation();

    if (!this.esproValue) {
      this.documentNumberTarget.value = "";
    }
  }

  documentTypeChanged() {
    this.clearValidation();
    this.applyMaskAndValidate();
  }

  documentNumberChanged() {
    this.applyMaskAndValidate();
  }

  applyMaskAndValidate() {
    const type = this.documentTypeTarget.value;
    const settings = this.config[type];
    let value = this.documentNumberTarget.value.replace(/\D/g, "");

    if (!settings) {
      this.clearValidation();
      return;
    }

    // Limitar tamanho e aplicar máscara
    value = value.slice(0, settings.length);
    this.documentNumberTarget.value = settings.mask(value);

    // Validar se estiver completo
    if (value.length === settings.length) {
      settings.validate(value) ? this.setValid() : this.setInvalid();
    } else {
      this.clearValidation();
    }
  }

  // VALIDATIONS
  isValidCPF(cpf) {
    // 123.456.789-09

    if (!cpf || cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += cpf[i] * (10 - i);
    let check = (sum * 10) % 11;
    if (check === 10) check = 0;
    if (check !== +cpf[9]) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += cpf[i] * (11 - i);
    check = (sum * 10) % 11;
    if (check === 10) check = 0;
    return check === +cpf[10];
  }

  isValidNIF(nif) {
    // 251 033 130

    if (
      !nif ||
      nif.length !== 9 ||
      !["1", "2", "3", "5", "6", "8", "9"].includes(nif[0])
    )
      return false;
    let sum = 0;
    for (let i = 0; i < 8; i++) sum += nif[i] * (9 - i);
    let check = 11 - (sum % 11);
    if (check >= 10) check = 0;
    return check === +nif[8];
  }

  isValidCUIT(cuit) {
    // cuit 20-30541334-3
    // cuil 27-25123456-4

    if (!cuit || cuit.length !== 11) return false;
    const factors = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 10; i++) sum += cuit[i] * factors[i];
    let check = 11 - (sum % 11);
    if (check === 11) check = 0;
    if (check === 10) check = 9;
    return check === +cuit[10];
  }

  // HELPERS
  setValid() {
    this.documentNumberTarget.classList.add("is-valid");
    this.documentNumberTarget.classList.remove("is-invalid");
  }

  setInvalid() {
    this.documentNumberTarget.classList.add("is-invalid");
    this.documentNumberTarget.classList.remove("is-valid");
    if (this.hasErrorMessageTarget) {
      const type = this.documentTypeTarget.value.toUpperCase();
      const invalidWord = this.invalidMessageValue || "inválido";
      this.errorMessageTarget.textContent = `${type} ${invalidWord}`;
    }
  }

  clearValidation() {
    this.documentNumberTarget.classList.remove("is-valid", "is-invalid");
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "";
    }
  }

  setDocumentTypeByCountryTarget() {
    const country = this.nationalityTarget.value;
    if (!country) return;
    this.documentCountryTarget.value = country;

    if (!!this.esproValue) {
      return this.setDocumentTypeOptions(["cpf"]);
    }

    switch (country) {
      case "BR":
        this.setDocumentTypeOptions(["cpf"]);
        break;
      case "AR":
        this.setDocumentTypeOptions(["cuit", "cuil"]);
        break;
      case "PT":
        this.setDocumentTypeOptions(["nif"]);
        break;
      default:
        this.setDocumentTypeOptions(["tax_id"]);
        break;
    }
  }

  setDocumentTypeOptions(allowedTypes) {
    const select = this.documentTypeTarget;
    Array.from(select.options).forEach((option) => {
      option.hidden = !allowedTypes.includes(option.value);
    });
    select.value = allowedTypes[0];
  }

  displayMigrationRegistrationInput() {
    if (
      !this.hasMigrationRegistrationNumberWrapperTarget ||
      !this.migrationRegistrationHiddenField
    ) {
      return;
    }

    const country = this.nationalityTarget.value;

    if (country === "BR" || !!this.esproValue) {
      this.migrationRegistrationHiddenField.remove();
    } else {
      this.migrationRegistrationNumberWrapperTarget.appendChild(
        this.migrationRegistrationHiddenField
      );
    }
  }
}
